import PropTypes from "prop-types";

/** Digits and at most one decimal point (optional leading "0" if user types "." first). */
function sanitizeCurrencyValue(raw) {
  if (raw == null || raw === "") return "";
  let s = String(raw).replaceAll(/[^\d.]/g, "");
  const dot = s.indexOf(".");
  if (dot !== -1) {
    s = `${s.slice(0, dot + 1)}${s.slice(dot + 1).replaceAll(".", "")}`;
  }
  if (s.startsWith(".")) {
    s = `0${s}`;
  }
  return s;
}

export default function FormCurrencyInput({
  id,
  label,
  name,
  value = "",
  onChange,
  placeholder,
  disabled,
  readOnly,
  error,
  className = "",
}) {
  const inputId = id || name;
  const isInert = disabled || readOnly;

  const handleChange = (event) => {
    if (isInert) return;
    const next = sanitizeCurrencyValue(event.target.value);
    onChange({
      ...event,
      target: { ...event.target, value: next, name },
      currentTarget: { ...event.currentTarget, value: next, name },
    });
  };

  const handleKeyDown = (event) => {
    if (isInert) return;
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    const nav = ["Backspace", "Delete", "Tab", "Escape", "Enter", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"];
    if (nav.includes(event.key)) return;

    if (/^\d$/.test(event.key)) return;

    if (event.key === "." && !String(value).includes(".")) return;

    event.preventDefault();
  };

  return (
    <div className={className}>
      <label htmlFor={inputId} className="mb-1 block text-xs font-semibold text-black">
        {label}
      </label>
      <div
        className={[
          "flex items-stretch overflow-hidden rounded-lg border transition ring-brand-600 focus-within:ring-2",
          error
            ? "border-red-400 bg-red-50/40"
            : isInert
              ? "cursor-not-allowed border-slate-300 bg-slate-100"
              : "border-slate-500 bg-white",
          isInert ? "focus-within:ring-0" : "",
        ].join(" ")}
      >
        <span
          className={[
            "flex shrink-0 items-center border-r px-3 text-sm font-medium",
            isInert
              ? "border-slate-300 bg-slate-200 text-text-500"
              : "border-slate-500 bg-slate-100 text-text-700",
          ].join(" ")}
        >
          $
        </span>
        <input
          id={inputId}
          name={name}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          pattern="[0-9]*\.?[0-9]*"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          aria-readonly={readOnly || undefined}
          aria-invalid={Boolean(error)}
          className={[
            "min-w-0 flex-1 border-0 px-3 py-2.5 text-sm outline-none",
            isInert ? "cursor-not-allowed bg-slate-100 text-text-600" : "bg-transparent",
          ].join(" ")}
        />
      </div>
      {error ? (
        <p id={`${inputId}-error`} className="mt-1 text-xs font-medium text-red-600" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

FormCurrencyInput.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  error: PropTypes.string,
  className: PropTypes.string,
};
